import { styled } from '@linaria/react';
import React from 'react';

export const ToolsContainer = styled.div`
    .row-fluid {
        width: 100%;
    }

    .mainbar {
        padding: 1rem;
    }

    .conversionCalculator {
        background: white;
    }

    .bordered-container {
        border: 1px solid #e9ecef;
        border-radius: 4px;
        background: white;
    }

    @media (max-width: 768px) {
        overflow-x: hidden;
        max-width: 100vw;
        box-sizing: border-box;

        .mainbar {
            padding: 0.5rem;
        }
    }
`;

export const ConversionTable = styled.table`
    width: 100%;
    margin-bottom: 0.5rem;
    border-collapse: collapse;
    background: white;

    thead {
        background: #f8f9fa;
        
        tr {
            border-bottom: 2px solid #e9ecef;
            
            th {
                padding: 0.5rem;
                text-align: left;
                font-weight: 600;
                color: #495057;
                font-size: 0.875rem;
            }

            td {
                padding: 0.5rem;
                text-align: right;
            }
        }
    }

    tbody {
        tr {
            border-bottom: 1px solid #e9ecef;

            &:last-child {
                border-bottom: none;
            }

            &:hover {
                background: #f8f9fa;
            }

            td {
                padding: 0.5rem;
                color: #495057;
                font-size: 0.875rem;

                &:first-child {
                    font-weight: 500;
                    width: 40%;
                }

                &:last-child {
                    text-align: right;
                }
            }
        }
    }

    @media (max-width: 768px) {
        table-layout: fixed;

        thead tr th,
        thead tr td,
        tbody tr td {
            padding: 0.35rem 0.25rem;
            font-size: 0.8rem;
        }

        tbody tr td:first-child {
            width: auto;
            word-wrap: break-word;
            overflow-wrap: break-word;
        }
    }
`;

export const GradientInputStyled = styled.input`
    width: 100px;
    text-align: right;
    padding: 0.4rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.875rem;
    background: white;
    
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
    }

    &:disabled {
        background: #e9ecef;
        color: #6c757d;
        cursor: not-allowed;
    }

    @media (max-width: 768px) {
        width: 70px;
        min-width: 50px;
        padding: 0.3rem;
        font-size: 0.8rem;
    }
`;

/**
 * Wrapper around GradientInputStyled that treats comma as decimal separator.
 * When the user presses the comma key, it is replaced with a dot so that
 * parseFloat works correctly regardless of keyboard layout.
 */
export const GradientInput = React.forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ',') {
            e.preventDefault();
            const input = e.currentTarget;
            const start = input.selectionStart ?? input.value.length;
            const end = input.selectionEnd ?? input.value.length;
            const newValue = input.value.slice(0, start) + '.' + input.value.slice(end);

            // Trigger the onChange with the modified value
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                'value'
            )?.set;
            nativeInputValueSetter?.call(input, newValue);
            input.dispatchEvent(new Event('input', { bubbles: true }));

            // Restore cursor position after the inserted dot
            requestAnimationFrame(() => {
                input.setSelectionRange(start + 1, start + 1);
            });
        }
        props.onKeyDown?.(e);
    };

    return <GradientInputStyled {...props} ref={ref} onKeyDown={handleKeyDown} />;
});

export const InputLabel = styled.label`
    font-size: 0.875rem;
    color: #495057;
    white-space: nowrap;
    margin-left: 0.5rem;
    font-weight: 500;
    width: 40px;
    text-align: left;

    @media (max-width: 768px) {
        font-size: 0.75rem;
        margin-left: 0.25rem;
        width: auto;
        min-width: 24px;
    }
`;

export const InputLabelWide = styled.label`
    font-size: 0.875rem;
    color: #495057;
    white-space: nowrap;
    margin-left: 0.5rem;
    font-weight: 500;
    width: 80px;
    text-align: left;

    @media (max-width: 768px) {
        font-size: 0.75rem;
        margin-left: 0.25rem;
        width: auto;
        min-width: 40px;
    }
`;

export const ResultSpan = styled.span`
    display: inline-block;
    width: 100px;
    text-align: right;
    padding: 0.4rem;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    font-size: 0.875rem;
    color: #495057;
    font-weight: 500;

    @media (max-width: 768px) {
        width: 70px;
        min-width: 50px;
        padding: 0.3rem;
        font-size: 0.8rem;
    }
`;

export const TabContent = styled.div`
    padding: 0.75rem;
    background: white;

    @media (max-width: 768px) {
        padding: 0.5rem 0.25rem;
        overflow-x: hidden;
    }
`;

export const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
`;

export const InputGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        gap: 0.15rem;
    }
`;

export const DensitySideBySide = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    border-top: 2px solid #ccc;
    padding-top: 0.5rem;
    margin-top: 1rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;
